import React, { useEffect, useRef, useState } from "react";
import UserProfessionalTable from "./tables/UserProfessionalTable";
import DatePicker from "react-tailwindcss-datepicker";
import {
  AdjustmentsVerticalIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import IconButton from "../../../iconButton";
import ExportAll from "../svgIcons/ExportAll";
import DropDownFilters from "./DropDownFilters";
import {
  useDeleteProfessionalMutation,
  useGetAllProfessionalsQuery,
  useLazyGetAllProfessionalsQuery,
  useLazyGetFilteredProfessionalsQuery,
} from "../../../../store/admin/professionalUserInfo";
import Loader from "../../../commons/Loader";
import { useGetAllUserStatusesQuery } from "../../../../store/admin/userStatuses";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import DeleteSmsModal from "../../../modal/DeleteSmsModal";

const ProfessionalsPage = () => {
  const dropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { statusTitle } = location?.state ?? {};
  const [filterActive, setFilterActive] = useState(false);
  const [allProfessionalsData, setAllProfessionalsData] = useState();
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [creditRenewal ,setCreditRenewal]=useState("")
  const [deleteLead, setDeleteLead] = useState(null);
  const [value, setValue] = useState({
    startDate: null,
    endDate: null,
  });

  const { data: allProfessionals, isLoading: allProfessionalsLoader } =
    useGetAllProfessionalsQuery();
  const [
    refetch,
    { data, isLoading: allProfessionalsRefetchLoader, isFetching },
  ] = useLazyGetAllProfessionalsQuery();

  const [
    getAllFilteredProfessionals,
    {
      data: filteredProfessionals,
      isLoading: filteredProfessionalsLoader,
      isSuccess: filteredProfessionalsSuccess,
    },
  ] = useLazyGetFilteredProfessionalsQuery();

  const { data: allUserStatuses } = useGetAllUserStatusesQuery();

  const [
    deleteProfessional,
    {
      data: deleteProfessionalData,
      isSuccess: deleteProfessionalSuccess,
      isLoading: deleteProfessionalLoader,
      isError: deleteProfessionalIsError,
      error: deleteProfessionalError,
    },
  ] = useDeleteProfessionalMutation();

  const handleOutsideClick = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setFilterActive(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (statusTitle && allUserStatuses) {
      const pendingStatus = allUserStatuses.find(
        (status) => status.code.toLowerCase() === "pending"
      );
      if (pendingStatus) {
        setSelectedStatus([pendingStatus]);
        getAllFilteredProfessionals({
          startDate: value.startDate,
          endDate: value.endDate,
          status_ids: pendingStatus.id,
        });
      }
    }
  }, [statusTitle, allUserStatuses]);

  useEffect(() => {
    if (filteredProfessionalsSuccess && !filteredProfessionalsLoader) {
      setFilterActive(false);
    }
  }, [filteredProfessionalsSuccess]);

  useEffect(() => {
    if (filteredProfessionals) {
      setAllProfessionalsData(filteredProfessionals);
    }
  }, [filteredProfessionals]);

  useEffect(() => {
    if (allProfessionals) {
      setAllProfessionalsData(allProfessionals);
    }
  }, [allProfessionals]);

  useEffect(() => {
    if (data) {
      setAllProfessionalsData(data);
    }
  }, [data]);

  useEffect(() => {
    if (isFetching) {
      setAllProfessionalsData({});
    }
  }, [isFetching]);

  useEffect(() => {
    if (!deleteProfessionalLoader) {
      if (deleteProfessionalSuccess) {
        toast.success(deleteProfessionalData.message);
        applyFiltered();
      } else if (deleteProfessionalIsError && deleteProfessionalError) {
        toast.error("Something went wrong, please try again");
      }
      setShowDeleteModal(false);
      setDeleteLead(null);
    }
  }, [deleteProfessionalSuccess, deleteProfessionalIsError]);

  const handleValueChange = (newValue) => {
    const isActiveStatus = selectedStatus.some(status => status.name === "Active");

    const filters = {
      startDate: newValue.startDate,
      endDate: newValue.endDate,
      status_ids: selectedStatus.map(status => status.id).join(","),
    };
  
    if (isActiveStatus) {
      filters.credit_renewal = creditRenewal ? 1 : 0;
    }

    getAllFilteredProfessionals(filters);
   
    setValue(newValue);
  };

  const applyFiltered = () => {
    const isActiveStatus = selectedStatus.some(status => status.name === "Active");
  
    const filters = {
      startDate: value.startDate,
      endDate: value.endDate,
      status_ids: selectedStatus.map(status => status.id).join(","),
    };
    // &&  typeof creditRenewal === "boolean")
    if (isActiveStatus) {
      filters.credit_renewal = creditRenewal ? 1 : 0;
    }
  
    getAllFilteredProfessionals(filters);
  };


  const handleProfessionalFilter = () => {
    setFilterActive((prevState) => !prevState);
  };

  const handleDropDownMenu = (eventType, data) => {
    switch (eventType) {
      case "delete":
        setShowDeleteModal(true);
        setDeleteLead(data);
        break;
      case "edit":
        navigate("/admin-dashboard/professional-profile", {
          state: {
            professionalId: data?.id,
          },
        });
        break;
    }
  };

  // if (
  //   allProfessionalsRefetchLoader ||
  //   allProfessionalsLoader ||
  //   filteredProfessionalsLoader
  // )
  //   return <Loader isLoading={true} />;

  return (
    <>
      <div className="col-span-12">
        <div className="flex justify-between items-start md:items-center flex-col md:flex-row gap-5 mb-5">
          <h3 className="text-xl leading-[160%] font-bold text-[#04091E]">
            Professionals
          </h3>
          <div className="flex items-start md:items-center gap-3.5 md:gap-[30px] flex-col md:flex-row md:max-w-[550px] justify-between w-full">
            <div className="rounded-md custom-date-picker cdp-new flex items-center md:max-w-[320px] w-full border border-[#ebeff3] bg-white py-2 h-12 cursor-pointer relative z-[1]">
              <div className="pr-3 mr-3 border-r border-[#EBEFF3] absolute left-5 -z-[1]">
                <CalendarDaysIcon className="w-6 h-6" />
              </div>
              <div className="w-full professional-datepicker">
                <DatePicker
                  primaryColor={"blue"}
                  value={value}
                  toggleClassName="hidden"
                  onChange={handleValueChange}
                  displayFormat={"MM/DD/YYYY"}
                  showShortcuts={true}
                  useRange={false}
                  maxDate={moment().toDate()}
                  readOnly
                />
              </div>
            </div>
            <div className="grid grid-cols-12 md:flex items-center gap-5 md:gap-[30px] w-full md:w-auto relative">
              <IconButton
                title="export all"
                icon={ExportAll}
                iconClasses="w-6 h-6"
                classes="text-sm font-bold leading-[160%] text-primary capitalize flex items-center justify-center gap-1.5 rounded-[10px] bg-[#FFFFFF] h-12 md:w-[122px] border border-primary col-span-6"
              />
              <button
                className={`md:w-12 h-12 flex items-center justify-center gap-2.5 md:gap-0 rounded-[10px] col-span-6 transition-all ${
                  filterActive
                    ? "bg-[#D2ECFF] text-primary"
                    : "bg-[#EBEFF3] text-[#485868]"
                }`}
                onClick={handleProfessionalFilter}
              >
                <AdjustmentsVerticalIcon className="w-6 h-6" />
                <span
                  className={`text-sm font-bold leading-[160%] block md:hidden ${
                    filterActive ? "text-primary" : "text-[#485868]"
                  }`}
                >
                  Filter
                </span>
              </button>
              <div
                ref={dropdownRef}
                className={`notificationDropdown absolute right-0 top-[55px] md:top-[60px] z-[380] opacity-0 ${
                  filterActive ? "notificationOpen" : ""
                }`}
              >
                <DropDownFilters
                  setFilterActive={setFilterActive}
                  filterActive={filterActive}
                  statusData={allUserStatuses}
                  selectedStatus={selectedStatus}
                  setSelectedStatus={setSelectedStatus}
                  applyFiltered={applyFiltered}
                  creditRenewal={creditRenewal}
                  setCreditRenewal={setCreditRenewal}
                />
              </div>
            </div>
          </div>
        </div>
          <UserProfessionalTable
            professionalData={allProfessionalsData}
            getUpdateData={(page, perPage) => {
              getAllFilteredProfessionals({
                page,
                perPage,
                startDate: value.startDate,
                endDate: value.endDate,
                status_ids: selectedStatus.map((status) => status.id).join(","),
              });
            }}
            handleDropDownMenu={handleDropDownMenu}
          />
      </div>
      <DeleteSmsModal
        isOpen={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDelete={() => deleteProfessional(deleteLead.id)}
        deleteLoader={deleteProfessionalLoader}
        text="Do you really want to delete this Professional?"
      />
    </>
  );
};

export default ProfessionalsPage;
